import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface ViewingGaugeProps {
    score: number | null
    verdict: string
    className?: string
}

export const verdictFor = (score: number | null): string => {
    if (score === null) return 'No forecast yet'
    if (score >= 8) return 'Excellent'
    if (score >= 6.2) return 'Good'
    if (score >= 4.2) return 'Fair'
    if (score >= 2.2) return 'Poor'

    return 'Clouded out'
}

/** An instrument dial from 0 to 10: how good the night looks for observing. */
export const ViewingGauge = ({ score, verdict, className }: ViewingGaugeProps): ReactNode => {
    const cx = 120
    const cy = 118
    const r = 96
    const ticks: ReactNode[] = []

    for (let step = 0; step <= 50; step++) {
        const value = step / 5
        const angle = Math.PI - (value / 10) * Math.PI
        const major = step % 5 === 0
        const inner = r - (major ? 11 : 6)

        ticks.push(
            <line
                key={step}
                x1={cx + Math.cos(angle) * inner}
                y1={cy - Math.sin(angle) * inner}
                x2={cx + Math.cos(angle) * r}
                y2={cy - Math.sin(angle) * r}
                stroke={major ? 'var(--c-star-3)' : 'var(--c-hair-2)'}
                strokeWidth={1}
            />
        )

        if (major && step % 25 === 0) {
            ticks.push(
                <text
                    key={`t-${step}`}
                    x={cx + Math.cos(angle) * (r - 24)}
                    y={cy - Math.sin(angle) * (r - 24) + 4}
                    textAnchor='middle'
                    fill='var(--c-star-3)'
                    fontFamily='var(--font-app-mono, monospace)'
                    fontSize='9'
                >
                    {value}
                </text>
            )
        }
    }

    const needle = score === null ? 0 : Math.max(0, Math.min(10, score))
    const rotation = -90 + needle * 18

    return (
        <span className={cn('relative block', className)}>
            <svg viewBox='0 0 240 132' className='block w-full' role='img' aria-label={score === null ? 'Viewing quality unavailable' : `Viewing quality ${needle.toFixed(1)} out of 10, ${verdict}`}>
                <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill='none' stroke='var(--c-hair-2)' strokeWidth={1} />
                <path d={`M ${cx - r + 18} ${cy} A ${r - 18} ${r - 18} 0 0 1 ${cx + r - 18} ${cy}`} fill='none' stroke='var(--c-hair)' strokeWidth={1} strokeDasharray='2 5' />
                {score !== null && (
                    <path
                        d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + Math.cos(Math.PI - (needle / 10) * Math.PI) * r} ${cy - Math.sin(Math.PI - (needle / 10) * Math.PI) * r}`}
                        fill='none'
                        stroke='var(--c-brass)'
                        strokeWidth={1.4}
                        className='transition-all duration-700'
                    />
                )}
                {ticks}
                <g
                    style={{ transform: `rotate(${rotation}deg)`, transformOrigin: `${cx}px ${cy}px`, transition: 'transform 1.2s cubic-bezier(0.22, 0.61, 0.36, 1)' }}
                    opacity={score === null ? 0.25 : 1}
                >
                    <line x1={cx} y1={cy} x2={cx} y2={cy - r + 16} stroke='var(--c-brass)' strokeWidth={1.2} />
                    <line x1={cx} y1={cy} x2={cx} y2={cy + 10} stroke='var(--c-brass)' strokeWidth={1.2} />
                </g>
                <circle cx={cx} cy={cy} r={4} fill='var(--c-ink)' stroke='var(--c-brass)' strokeWidth={1.2} />
            </svg>
            <span className='mt-1 flex items-baseline justify-center gap-3'>
                <span className='font-heading text-4xl font-light text-star'>
                    {score === null ? '—' : needle.toFixed(1)}
                </span>
                <span className='label-mono text-brass'>
                    {verdict}
                </span>
            </span>
        </span>
    )
}
